import { useEffect, useState } from "react";
import { ExternalLink, Camera, Instagram, Facebook, MessageCircle, Globe, Sparkles, Loader2, Copy, ShieldCheck, Radio, BookOpen } from "lucide-react";
import { toast } from "sonner";
import { getProfile, coachProfile } from "../lib/api";
import { T } from "@/locales/tr";

const platformIcon = {
  "Site officiel": Globe,
  "Instagram": Instagram,
  "Facebook": Facebook,
  "TikTok": Radio,
  "WhatsApp Business": MessageCircle,
};

const platformKey = {
  "Site officiel": "website",
  "Instagram": "instagram",
  "Facebook": "facebook",
  "TikTok": "tiktok",
  "WhatsApp Business": "whatsapp",
};

export default function Reseaux() {
  const [profile, setProfile] = useState(null);
  const [platform, setPlatform] = useState("instagram");
  const [bio, setBio] = useState("");
  const [objective, setObjective] = useState("Attirer plus de clients photo & DA et créer des liens authentiques");
  const [result, setResult] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => { getProfile().then(setProfile).catch(() => {}); }, []);

  const ask = async () => {
    if (!bio.trim()) { toast.error(T("Colle ta bio ou un échantillon de contenu à analyser.")); return; }
    setLoading(true); setResult("");
    try {
      const data = await coachProfile({ platform, bio_or_content: bio, objective });
      setResult(data.text);
      toast.success(T("Diagnostic prêt."));
    } catch { toast.error(T("Papicoo n'a pas pu analyser. Réessaie.")); }
    finally { setLoading(false); }
  };

  return (
    <div data-testid="page-reseaux" className="max-w-6xl mx-auto px-4 sm:px-8 py-10">
      <div className="text-[11px] uppercase tracking-[0.22em] text-[color:var(--papicoo-accent)]">{T("Mes Réseaux")}</div>
      <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl text-[color:var(--papicoo-ink)] mt-2">
        {T("Là où l'on me trouve.")}
      </h1>
      <p className="text-[color:var(--papicoo-muted)] mt-3 max-w-2xl leading-relaxed">
        {T(profile?.role || "Directeur Artistique · Photographe · Web Designer · Maquettiste")} — {profile?.city || "Paris"}. {T("Mes liens publics, mon portfolio, et le coach IA qui te dit comment je peux faire mieux — sans jamais trahir ma voix ni ma foi.")}
      </p>

      {profile?.expertise?.length > 0 && (
        <div className="mt-5 flex flex-wrap gap-2">
          {profile.expertise.map((e) => (
            <span key={e.label} className="px-3 py-1.5 rounded-full bg-white border border-[color:var(--papicoo-border)] text-xs text-[color:var(--papicoo-ink)]">
              {T(e.label)}
            </span>
          ))}
        </div>
      )}

      {/* Cards des liens */}
      <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {profile?.links.map((link) => {
          const Icon = platformIcon[link.platform] || Globe;
          return (
            <a
              key={link.platform}
              href={link.url}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`link-${platformKey[link.platform]}`}
              className="papicoo-card p-5 hover:-translate-y-1 transition-transform duration-500 group"
            >
              <div className="flex items-center justify-between">
                <Icon size={22} className="text-[color:var(--papicoo-gold)]" strokeWidth={1.5} />
                <ExternalLink size={14} className="text-[color:var(--papicoo-muted)] group-hover:text-[color:var(--papicoo-ink)] transition-colors" />
              </div>
              <div className="font-display text-xl text-[color:var(--papicoo-ink)] mt-4">{link.platform}</div>
              <div className="text-sm text-[color:var(--papicoo-muted)] mt-1">{T(link.handle)}</div>
              <div className="text-xs text-[color:var(--papicoo-muted)] mt-3 italic">{T(link.note)}</div>
            </a>
          );
        })}
      </div>

      <div className="mt-3 inline-flex items-center gap-2 text-xs text-[color:var(--papicoo-muted)]">
        <ShieldCheck size={14} className="text-[color:var(--papicoo-ink)]" />
        {T("Papicoo ne lit jamais tes comptes privés. Seuls les liens publics sont affichés.")}
      </div>

      {/* Libraries */}
      {profile?.libraries?.length > 0 && (
        <section className="mt-10">
          <div className="text-[11px] uppercase tracking-[0.22em] text-[color:var(--papicoo-accent)]">{T("Bibliothèque officielle")}</div>
          <div className="mt-3 grid sm:grid-cols-2 gap-4">
            {profile.libraries.map((lib) => (
              <a
                key={lib.url}
                href={lib.url}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="link-library-hub"
                className="papicoo-card p-5 hover:-translate-y-1 transition-transform duration-500 group"
              >
                <div className="flex items-center justify-between">
                  <BookOpen size={22} className="text-[color:var(--papicoo-gold)]" />
                  <ExternalLink size={14} className="text-[color:var(--papicoo-muted)] group-hover:text-[color:var(--papicoo-ink)]" />
                </div>
                <div className="font-display text-xl text-[color:var(--papicoo-ink)] mt-3">{T(lib.name)}</div>
                <div className="text-sm text-[color:var(--papicoo-muted)] mt-2 leading-relaxed">{T(lib.description)}</div>
              </a>
            ))}
          </div>
        </section>
      )}

      {/* Portfolio teaser */}
      {profile?.portfolio_images?.length > 0 && (
        <section className="mt-14">
          <div className="flex items-end justify-between gap-4 mb-5">
            <div>
              <div className="text-[11px] uppercase tracking-[0.22em] text-[color:var(--papicoo-accent)]">{T("Portfolio")}</div>
              <h2 className="font-display text-3xl text-[color:var(--papicoo-ink)] mt-1 flex items-center gap-2">
                <Camera size={24} className="text-[color:var(--papicoo-gold)]" />
                {T("Aperçu — depuis papicoo.fr")}
              </h2>
            </div>
            <a href="https://www.papicoo.fr" target="_blank" rel="noopener noreferrer" data-testid="portfolio-cta" className="hidden sm:inline-flex items-center gap-2 text-sm papicoo-link-underline text-[color:var(--papicoo-ink)]">
              {T("Voir tout le site")} <ExternalLink size={14} />
            </a>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {profile.portfolio_images.map((src, i) => (
              <div key={src} className="papicoo-card overflow-hidden aspect-[4/5]">
                <img src={src} alt={`Papicoo portfolio ${i + 1}`} loading="lazy" className="w-full h-full object-cover hover:scale-105 transition-transform duration-700" />
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Coach de profil */}
      <section className="mt-16">
        <div className="text-[11px] uppercase tracking-[0.22em] text-[color:var(--papicoo-accent)]">{T("Coach de profil — Papicoo IA")}</div>
        <h2 className="font-display text-3xl sm:text-4xl text-[color:var(--papicoo-ink)] mt-1">
          {T("Améliore une plateforme en une minute.")}
        </h2>

        <div className="mt-6 grid lg:grid-cols-2 gap-6">
          <div className="papicoo-card p-6 space-y-4">
            <div>
              <label className="text-[11px] uppercase tracking-[0.2em] text-[color:var(--papicoo-muted)]">{T("Plateforme")}</label>
              <div className="mt-2 flex flex-wrap gap-2">
                {[
                  { v: "instagram", l: "Instagram" },
                  { v: "tiktok", l: "TikTok" },
                  { v: "facebook", l: "Facebook" },
                  { v: "whatsapp", l: "WhatsApp Business" },
                  { v: "website", l: "Site papicoo.fr" },
                ].map((p) => (
                  <button
                    key={p.v}
                    data-testid={`coach-platform-${p.v}`}
                    onClick={() => setPlatform(p.v)}
                    className={`px-3 py-2 text-xs rounded-full ${platform === p.v ? "papicoo-btn-gold" : "bg-white border border-[color:var(--papicoo-border)]"}`}
                  >
                    {p.l}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-[0.2em] text-[color:var(--papicoo-muted)]">{T("Objectif")}</label>
              <input
                data-testid="coach-objective"
                value={objective}
                onChange={(e) => setObjective(e.target.value)}
                className="w-full mt-2 bg-[color:var(--papicoo-sand)] border border-[color:var(--papicoo-border)] rounded-lg p-3 text-sm focus:outline-none focus:ring-2 focus:ring-[color:var(--papicoo-ink)]/30"
              />
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-[0.2em] text-[color:var(--papicoo-muted)]">{T("Bio ou échantillon de contenu")}</label>
              <textarea
                data-testid="coach-bio"
                value={bio}
                onChange={(e) => setBio(e.target.value)}
                rows={8}
                className="w-full mt-2 bg-[color:var(--papicoo-sand)] border border-[color:var(--papicoo-border)] rounded-lg p-4 text-sm focus:outline-none focus:ring-2 focus:ring-[color:var(--papicoo-ink)]/30"
                placeholder={T("Colle ici ta bio Instagram, ton 'à propos' Facebook, ou un de tes posts.")}
              />
            </div>
            <button data-testid="coach-go-btn" onClick={ask} disabled={loading} className="inline-flex items-center gap-2 px-5 py-3 rounded-full papicoo-btn-primary disabled:opacity-50">
              {loading ? <Loader2 size={16} className="animate-spin" /> : <Sparkles size={16} />}
              {T("Demander le diagnostic à Papicoo")}
            </button>
          </div>

          <div data-testid="coach-result">
            {result ? (
              <div className="papicoo-card p-6 max-h-[80vh] overflow-y-auto">
                <div className="flex items-center justify-between mb-3">
                  <div className="font-display text-2xl text-[color:var(--papicoo-ink)]">{T("Diagnostic")}</div>
                  <button data-testid="copy-coach" onClick={() => { navigator.clipboard.writeText(result); toast.success(T("Copié.")); }} className="text-xs inline-flex items-center gap-1 text-[color:var(--papicoo-muted)] hover:text-[color:var(--papicoo-ink)]">
                    <Copy size={14} /> {T("Copier")}
                  </button>
                </div>
                <pre className="whitespace-pre-wrap font-sans text-[15px] leading-relaxed">{result}</pre>
              </div>
            ) : (
              <div className="papicoo-card p-6 text-sm italic text-[color:var(--papicoo-muted)]">
                {T("Force tranquille — colle un contenu, choisis la plateforme, et Papicoo te donne diagnostic, bio réécrite, idées de posts et réponse-type aux DM.")}
              </div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
